/**
 * Download prepared model artifacts (zip + docker tar) with in-app byte progress.
 *
 * Both artifact types are streamed same-origin through ``/download-model`` so
 * the browser can report real byte progress as it writes to disk. A signed
 * object-storage URL would be fetched cross-origin and is blocked by CORS on
 * most buckets, which silently degraded the zip download into a progress-less
 * browser-toolbar transfer. Large artifacts would crash the renderer if buffered
 * in a Blob, so the streaming writer REQUIRES a File System Access
 * ``WritableStream`` and never falls back to in-memory accumulation. When that
 * API is unavailable (Firefox / Safari / older Chromium) the caller falls back
 * to a native browser download via the GET ``/download-model`` route.
 */

import { get_base_url } from '@/shared/http/client';
export class PickerUnavailableError extends Error {
    constructor(message = 'File System Access save dialog is not available') {
        super(message);
        this.name = 'PickerUnavailableError';
    }
}

export function computeDownloadPercent(received, total) {
    if (!total || total <= 0 || received < 0) {
        return 0;
    }
    return Math.max(0, Math.min(100, Math.round((received / total) * 100)));
}

function resolveTotalBytes(response, totalBytes) {
    if (typeof totalBytes === 'number' && totalBytes > 0) {
        return totalBytes;
    }
    const contentLength = response.headers?.get('content-length');
    if (contentLength) {
        const parsed = parseInt(contentLength, 10);
        if (!Number.isNaN(parsed) && parsed > 0) {
            return parsed;
        }
    }
    return 0;
}

function triggerAnchorDownload(url, filename) {
    const anchor = document.createElement('a');
    anchor.href = url;
    if (filename) {
        anchor.download = filename;
    }
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
}

function buildDownloadModelGetUrl(payload) {
    const params = new URLSearchParams();
    Object.entries(payload || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
        }
    });
    return `${get_base_url(window.location.origin)}/download-model?${params.toString()}`;
}

/**
 * Open the File System Access save dialog. MUST be called synchronously from a
 * user-gesture handler (click) so the browser's transient activation is still
 * valid. Returns the writable stream on success, ``null`` if the user cancels,
 * and throws ``PickerUnavailableError`` if the API isn't supported.
 */
export async function requestSaveTarget(filename) {
    const canUseFilePicker =
        typeof window !== 'undefined' && typeof window.showSaveFilePicker === 'function';
    if (!canUseFilePicker) {
        throw new PickerUnavailableError();
    }
    try {
        const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [
                {
                    description: 'Model archive',
                    accept: {
                        'application/x-tar': ['.tar'],
                        'application/zip': ['.zip'],
                        'application/octet-stream': ['.tar', '.zip'],
                    },
                },
            ],
        });
        return await handle.createWritable();
    } catch (err) {
        if (err?.name === 'AbortError') {
            return null;
        }
        // SecurityError / NotAllowedError typically mean the user activation
        // expired (e.g. picker invoked outside a click handler). Treat the same
        // as "unavailable" so callers fall back to the anchor download path.
        throw new PickerUnavailableError(err?.message || 'showSaveFilePicker failed');
    }
}

/**
 * Stream an HTTP response straight to a pre-acquired ``WritableStream``.
 *
 * The writable handle is REQUIRED — there is intentionally no in-memory
 * fallback because multi-GB Docker tars would OOM the renderer if buffered.
 */
async function streamHttpResponseToDisk(response, { writable, onProgress, totalBytes } = {}) {
    if (!response.ok) {
        try {
            await writable?.abort?.();
        } catch {
            // best-effort
        }
        throw new Error(`Download failed (HTTP ${response.status})`);
    }
    if (!writable) {
        throw new Error(
            'streamHttpResponseToDisk requires a pre-acquired writable handle (call requestSaveTarget first)'
        );
    }

    const total = resolveTotalBytes(response, totalBytes);
    const reader = response.body?.getReader();
    if (!reader) {
        try {
            await writable.abort?.();
        } catch {
            // best-effort
        }
        throw new Error('Streaming download is not supported in this browser');
    }

    let received = 0;
    if (onProgress) {
        onProgress(0);
    }

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            await writable.write(value);
            received += value.length;
            if (onProgress) {
                onProgress(computeDownloadPercent(received, total));
            }
        }
        await writable.close();
    } catch (err) {
        try {
            await writable.abort?.();
        } catch {
            // best-effort
        }
        throw err;
    }

    if (onProgress) {
        onProgress(100);
    }
    return { success: true, indeterminate: false };
}

/**
 * Download a prepared model artifact (zip or docker tar).
 *
 * Caller is expected to have already acquired ``writable`` via
 * ``requestSaveTarget`` from a user-gesture click handler. If the writable is
 * absent (File System Access API unsupported — Firefox / Safari / older
 * Chromium), this navigates the browser to the same-origin GET
 * ``/download-model`` URL so the browser's native download manager handles the
 * transfer (progress shows in the browser toolbar, not in-app).
 *
 * When a writable is present, zip artifacts stream same-origin through POST
 * ``/download-model`` so byte progress is reported via ``onProgress``.
 * Docker tars always use the native GET (even if a writable was passed): a
 * ~9GB File System Access fetch dies with ``TypeError: network error`` while
 * the bake is already in object storage. GET 302s to a signed URL.
 * The route returns either:
 *   - JSON ``{ delivery_mode: "local_path", local_file_path, ... }`` (single-node)
 *   - Binary stream (cloud zip, ``Content-Disposition: attachment``)
 */
export async function downloadPreparedModelArtifact(
    payload,
    filename,
    { writable, onProgress, totalBytes } = {}
) {
    const useNativeGet = payload?.artifact_type === 'docker' || !writable;
    if (useNativeGet) {
        if (writable) {
            try {
                await writable.abort?.();
            } catch {
                // best-effort — we will not write the picker file
            }
        }
        triggerAnchorDownload(buildDownloadModelGetUrl(payload), filename);
        if (onProgress) {
            onProgress(0);
        }
        return { success: true, indeterminate: true, deliveryMode: 'anchor_get' };
    }

    let response;
    try {
        response = await fetch(`${get_base_url(window.location.origin)}/download-model`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            credentials: 'include',
        });
    } catch (err) {
        try {
            await writable.abort?.();
        } catch {
            // best-effort
        }
        throw err;
    }

    const contentType = response.headers.get('content-type') || '';

    if (contentType.includes('application/json')) {
        // Local-storage single-node hands back an absolute path instead of a
        // stream; release the picker handle since we won't write chunks here.
        try {
            await writable.abort?.();
        } catch {
            // best-effort
        }

        const json = await response.json();
        if (!response.ok || !json.success) {
            throw new Error(json.message || `Download failed (HTTP ${response.status})`);
        }
        if (json.delivery_mode === 'local_path') {
            return {
                success: true,
                deliveryMode: 'local_path',
                localFilePath: json.local_file_path,
                sizeBytes: json.size_bytes,
            };
        }
        throw new Error(json.message || 'Unexpected download response');
    }

    const streamTotal = totalBytes ?? (() => {
        const cl = response.headers.get('content-length');
        if (!cl) return undefined;
        const parsed = parseInt(cl, 10);
        return Number.isNaN(parsed) ? undefined : parsed;
    })();

    const streamed = await streamHttpResponseToDisk(response, {
        writable,
        onProgress,
        totalBytes: streamTotal,
    });
    return { ...streamed, deliveryMode: 'api_stream' };
}

// Exported for unit tests only.
export const __TEST__ = {
    streamHttpResponseToDisk,
    buildDownloadModelGetUrl,
};
